import { clsx } from 'clsx';
import { Packer } from 'docx';
import { Col, Row } from 'antd';
import { saveAs } from 'file-saver';
import { useController } from 'react-hook-form';
import React, { useEffect, useState } from 'react';

import { TableCrop } from './components/TableCrop';
import { TableFiscal } from './components/TableFiscal';
import { ExportRisk } from '@pages/credit-risk/export';
import { DownloadZone } from './components/DownloadZone';
import { DropdownTable } from './components/DropdownTable';
import { DownloadBlock } from './components/DownloadBlock';
import { RiskGeneral } from '@pages/credit-risk/RiskGeneral';
import { TablePastCycle } from './components/TablePastCycle';
import { CreditRisk, fixedRiskText, SurveyQuestion } from '@types';
import { ScoreFromAdminRisk } from './components/ScoreFromAdminRisk';
import { TableProjectedCycle } from './components/TableProjectedCycle';
import { AddemVariablesTable } from './components/AddemVariablesTable';
import { useDebounce, useIsFirstRenderTimeout, useRequest } from '@hooks';
import { CreditBureauForm } from '@pages/credit-risk/analysis/components/CreditBureauForm';
import { CreditSurveyView } from '@pages/credit/CreditEdit/CreditSurveyView';
import { DocumentCreator } from '@pages/credit/CreditSurvey/document-creator';
import { Container, InitialCard, PageHeader, ScoreWidget } from '@components';
import {
  creditHttp,
  CreditReportTypes,
  creditRiskHttp,
  ReportDocumentsProps,
  surveyHttp,
} from '@network';
import { Progress } from '@pages/credit-risk/analysis/components/Progress';
import { FinancialRatios } from '@pages/credit-risk/analysis/components/FinancialRatios';
import cls from './analysis.module.css';

export const Analysis = ({ risk, readonly }: {risk: CreditRisk; readonly?: boolean}) => {
  const [riskCalculated, setRiskCalculated] = useState<CreditRisk>(risk);
  const [isReportOpened, setReportOpened] = useState(false);
  const [documents, setDocuments] = useState<ReportDocumentsProps | null>(null);
  const [creditDocs, setCreditDocs] = useState(0);
  const [guarantorsDocs, setGuarantorsDocs] = useState(0);
  const openReport = risk ? () => setReportOpened(true) : undefined;
  const [questions, setQuestions] = useState<SurveyQuestion[] | null>(null);
  const score = risk.riskScore || 0;
  const backTo = `/credit-risk/${risk.id}`;
  const leftCardsLabelGrid = { xs: 12, lg: 24, xl: 8, xxl: 7 };
  const leftCardsValueGrid = { xs: 12, lg: 24, xl: 16, xxl: 17 };

  const {
    field: { value: tablePastCycle, onChange: changeTablePastCycle },
  } = useController({ name: 'tablePastCycle' });
  const {
    field: { value: tableProjectedCycle, onChange: changeTableProjectedCycle },
  } = useController({ name: 'tableProjectedCycle' });
  const {
    field: { value: tableCrop, onChange: changeTableCrop },
  } = useController({ name: 'tableCrop' });
  const {
    field: { value: tableFiscal, onChange: changeTableFiscal },
  } = useController({ name: 'tableFiscal' });
  const {
    field: { value: tableCreditBureau, onChange: changeTableCreditBureau },
  } = useController({ name: 'tableCreditBureau' });
  const {
    field: { value: tableCreditBureauScore, onChange: changeTableCreditBureauScore },
  } = useController({ name: 'tableCreditBureauScore' });
  const {
    field: { value: addemVariables, onChange: changeAddemVariablesTable },
  } = useController({ name: 'addemVariables' });
  const {
    field: { value: totalVariables, onChange: changeTotalVariablesTable },
  } = useController({ name: 'totalVariables' });
  const {
    field: { value: replyOther, onChange: changeReplyOther },
  } = useController({ name: 'replyOther' });
  const {
    field: { value: replyScore, onChange: changeReplyScore },
  } = useController({ name: 'replyScore' });
  const {
    field: { value: creditBureau, onChange: changeCreditBureau },
  } = useController({ name: 'creditBureau', defaultValue: risk.creditBureau });
  const {
    field: { value: creditBureauAnualRevenue, onChange: changeCreditBureauAnualRevenue },
  } = useController({
    name: 'creditBureauAnualRevenue',
    defaultValue: risk.creditBureauAnualRevenue,
  });

  const debouncedTablePastCycle = useDebounce(tablePastCycle, 1200);
  const debouncedTableProjectedCycle = useDebounce(tableProjectedCycle, 1200);
  const debouncedTableCrop = useDebounce(tableCrop, 1200);
  const debouncedTableFiscal = useDebounce(tableFiscal, 1200);
  const debouncedTableCreditBureau = useDebounce(tableCreditBureau, 1200);
  const debouncedTableCreditBureauScore = useDebounce(tableCreditBureauScore, 1200);
  const debouncedTotalVariables = useDebounce(totalVariables, 1200);
  const isFirstRender = useIsFirstRenderTimeout(2000);

  const {
    submit: downloadBureauReport, loading: downloadBureauReportLoading,
  } = useRequest(async () => {
    await creditHttp.downloadReportFile({
      type: CreditReportTypes.BUREAU_REPORT,
      creditId: risk.creditId,
    });
  });

  const {
    submit: downloadSinectaReport, loading: downloadSinectaReportLoading,
  } = useRequest(async () => {
    await creditHttp.downloadReportFile({
      type: CreditReportTypes.SINECTA_REPORT,
      creditId: risk.creditId,
    });
  });

  const {
    submit: downloadEkatenaReport, loading: downloadEkatenaReportLoading,
  } = useRequest(async () => {
    await creditHttp.downloadReportFile({
      type: CreditReportTypes.EKATENA_REPORT,
      creditId: risk.creditId,
    });
  });

  const { submit: downloadDocuments, loading: downloadDocumentsLoading } = useRequest(async () => {
    await creditHttp.downloadCreditDocuments(risk.creditId);
  });

  const { submit: downloadOpinion, loading: downloadOpinionLoading } = useRequest(async () => {
    await creditHttp.downloadOpinion(risk.creditId);
  });

  const { submit: downloadCSF, loading: downloadCSFLoading } = useRequest(async () => {
    await creditHttp.downloadCSF(risk.creditId);
  });

  const generateDocx = () => {
    if (questions) {
      const documentCreator = new DocumentCreator();
      const doc = documentCreator.createFromQeustions(questions, risk.credit);

      Packer.toBlob(doc).then(blob => {
        saveAs(blob, `${risk.credit.user.fullName}. Questionario of Credit ${risk.credit.code}.docx`);
        console.log('Document created successfully');
      });
    }
  };

  const {
    submit: downloadAllGuarantorDocuments,
    loading: downloadAllGuarantorDocumentsLoading,
  } = useRequest(async () => {
    await creditHttp.downloadAllGuarantorDocuments(risk.creditId, risk.credit.code);
  });

  const getDocsCounter = (type: CreditReportTypes): number | undefined => {
    return !documents ? undefined : documents[type].length;
  };

  useEffect(() => {
    (async () => {
      try {
        const loadedSurvey = await surveyHttp.getCreditSurvey(risk.creditId);
        const docsData: ReportDocumentsProps = await creditHttp.reportFiles(risk.creditId);
        const creditDocsCounter: number = await creditHttp.creditDocuments(risk.creditId);
        const guarantorsDocsCounter: number = await creditHttp.guarantorsDocuments(risk.creditId);

        setCreditDocs(typeof creditDocsCounter === 'number' ? creditDocsCounter : 0);
        setGuarantorsDocs(typeof guarantorsDocsCounter === 'number' ? guarantorsDocsCounter : 0);

        if (loadedSurvey && loadedSurvey?.surveyQuestions.length) {
          setQuestions(loadedSurvey.surveyQuestions);
        }

        if (docsData) {
          setDocuments(docsData);
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, [risk]);

  useEffect(() => {
    !isFirstRender && (async () => {
      try {
        const calculated = await creditRiskHttp.calculate(risk.id, {
          tablePastCycle,
          tableProjectedCycle,
          tableCrop,
          tableFiscal,
          tableCreditBureau,
          tableCreditBureauScore,
          totalVariables,
        });
        const s = (obj: any) => JSON.stringify(obj || {});

        if (!calculated) {
          throw new Error('No result');
        }

        if (s(tablePastCycle) !== s(calculated.tablePastCycle)) {
          changeTablePastCycle(calculated.tablePastCycle);
        }
        if (s(tableProjectedCycle) !== s(calculated.tableProjectedCycle)) {
          changeTableProjectedCycle(calculated.tableProjectedCycle);
        }
        if (s(tableCreditBureau) !== s(calculated.tableCreditBureau)) {
          changeTableCreditBureau(calculated.tableCreditBureau);
        }
        if (s(tableFiscal) !== s(calculated.tableFiscal)) {
          changeTableFiscal(calculated.tableFiscal);
        }
        setRiskCalculated(calculated);
        changeCreditBureauAnualRevenue(calculated.creditBureauAnualRevenue);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [
    debouncedTablePastCycle,
    debouncedTableProjectedCycle,
    debouncedTableCrop,
    debouncedTableFiscal,
    debouncedTableCreditBureau,
    debouncedTableCreditBureauScore,
    debouncedTotalVariables,
    isFirstRender,
  ]);

  const isFixed = risk.isFixed || !!readonly;
  const annualRevenue = Array.isArray(tableProjectedCycle?.rows)
    ? tableProjectedCycle.rows.find(i => i.title === 'Adjusted Global Net Income')
    : null;
  const noiRate = Array.isArray(tableProjectedCycle?.rows)
    ? tableProjectedCycle.rows.find(i => i.title === 'Adjusting Variable Risk')
    : null;
  const match = risk?.creditTerm?.match(/^\s*([1-9]|1[0-2])\s+Meses\s*$/i) || [];
  const creditTermValue = match ? Number(match[1]) : 0;

  return (
    <>
      <PageHeader
        title="Analysis"
        linkText="Back to risk page"
        linkUrl={backTo}
      />

      <Container max>
        <Row gutter={24} align={'stretch'} className={cls.row}>
          <Col xs={24} xl={16} className={cls.leftCol}>
            {risk ? <RiskGeneral
              risk={risk}
              isFixed={isFixed}
              labelGrid={leftCardsLabelGrid}
              valueGrid={leftCardsValueGrid}
            /> : null}
          </Col>

          <Col xs={24} xl={8} className={clsx(cls.rightCol, cls.colFill, cls.stretch)}>
            <div className={clsx(cls.colored, cls.bordered, cls.aside, cls.padding, cls.asideWrap)}>
              <InitialCard center="Score Total" riskPadding wrapperClass={cls.scoreCard}>
                <ScoreWidget
                  value={score}
                  percents={score > risk.successPercent ? 90 : 10}
                  className={cls.scoreWidget}
                  onClick={openReport}
                />
              </InitialCard>

              <DownloadZone className={cls.dlZone}>
                <DownloadBlock
                  title="SAT Report"
                  onClick={downloadEkatenaReport}
                  isLoading={downloadEkatenaReportLoading}
                  counter={getDocsCounter(CreditReportTypes.EKATENA_REPORT)}
                />
                <DownloadBlock
                  title="Bureau Report"
                  onClick={downloadBureauReport}
                  isLoading={downloadBureauReportLoading}
                  counter={getDocsCounter(CreditReportTypes.BUREAU_REPORT)}
                />
                <DownloadBlock
                  title="Opinión de Cumplimiento"
                  onClick={downloadOpinion}
                  isLoading={downloadOpinionLoading}
                />
                <DownloadBlock
                  title="CSF del Cliente"
                  onClick={downloadCSF}
                  isLoading={downloadCSFLoading}
                />
                <DownloadBlock
                  title="Field Report"
                  onClick={downloadSinectaReport}
                  isLoading={downloadSinectaReportLoading}
                  counter={getDocsCounter(CreditReportTypes.SINECTA_REPORT)}
                />
                <DownloadBlock
                  title="All Client Files"
                  onClick={downloadDocuments}
                  isLoading={downloadDocumentsLoading}
                  counter={creditDocs}
                />
                <DownloadBlock
                  title="All Guarantor Files"
                  onClick={downloadAllGuarantorDocuments}
                  isLoading={downloadAllGuarantorDocumentsLoading}
                  counter={guarantorsDocs}
                />
                <DownloadBlock
                  title="Survey PDF"
                  onClick={generateDocx}
                  isLoading={false}
                >
                  <CreditSurveyView
                    credit={risk.credit}
                    hide
                    btnText={
                      <svg className={cls.icon}
                        width="1em"
                        height="1em"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <path className={cls.iconPath}
                          fill="#042E6B"
                          d="M16 0C20.4183 0 24 3.58172 24 8V16C24 20.4183 20.4183 24 16 24H8C3.58172 24 1.28855e-07 20.4183 0 16V8C0 3.58172 3.58172 1.28851e-07 8 0H16ZM12.0195 5.00098C11.4147 5.02287 10.9419 5.55259 10.9629 6.18359V15.6709L8.82129 13.6826C8.36967 13.263 7.67812 13.3046 7.27637 13.7764C6.87462 14.2481 6.91558 14.971 7.36719 15.3906L10.585 18.3955C10.9483 18.76 11.4262 18.9745 11.9297 18.998C11.9679 19.0003 12.0067 19.0003 12.0449 18.998C12.089 18.9973 12.1337 18.9943 12.1777 18.9883C12.6805 18.955 13.1539 18.7326 13.5107 18.3613L16.6758 15.0645H16.6768C17.1052 14.6199 17.1082 13.8956 16.6826 13.4473C16.257 12.9991 15.5639 12.9969 15.1348 13.4414L13.1514 15.5137V6.10449C13.1304 5.4729 12.6234 4.97936 12.0195 5.00098Z" />
                      </svg>
                    }
                    btnClass={cls.surveyExport}
                  />
                </DownloadBlock>
              </DownloadZone>
            </div>
          </Col>
        </Row>

        <Row className={clsx(cls.row, cls.marginTop)}>
          <Col xs={24} className={cls.row}>
            <InitialCard left="Income Verification" riskPadding
              lockText={risk.isFixed ? fixedRiskText : null}>
              <Row gutter={16} className={cls.row} style={{ marginTop: 22 }}>
                <Col xs={0} sm={2} />
                <Col xs={24} sm={10} className={cls.tableCol}>
                  <TablePastCycle
                    data={tablePastCycle}
                    onChange={changeTablePastCycle}
                    title="Past Cycle"
                    isFixed={isFixed}
                  />
                </Col>
                <Col xs={24} sm={10} className={cls.tableCol}>
                  <TableProjectedCycle
                    data={tableProjectedCycle}
                    onChange={changeTableProjectedCycle}
                    title="Projected Cycle"
                    isFixed={isFixed}
                  />
                </Col>
                <Col xs={0} sm={2} />
              </Row>
            </InitialCard>
          </Col>
        </Row>

        <Row className={clsx(cls.row, cls.marginTop)}>
          <Col xs={24} className={cls.row}>
            <InitialCard left="Summary of  Fiscal Income & Expenses" riskPadding
              lockText={risk.isFixed ? fixedRiskText : null}>
              <TableFiscal
                data={tableFiscal}
                onChange={changeTableFiscal}
                isFixed={isFixed}
              />
            </InitialCard>
          </Col>
        </Row>

        <Row className={clsx(cls.row, cls.marginTop)}>
          <Col xs={24} className={cls.row}>
            <InitialCard left="Fiscal Income of Crop to Finance" riskPadding
              lockText={risk.isFixed ? fixedRiskText : null}>
              <TableCrop
                data={tableCrop}
                onChange={changeTableCrop}
                isFixed={isFixed}
              />
            </InitialCard>
          </Col>
        </Row>

        <Row className={clsx(cls.row, cls.marginTop)}>
          <Col xs={24} className={cls.row}>
            <InitialCard left="Credit bureau" riskPadding
              lockText={risk.isFixed ? fixedRiskText : null}>
              <CreditBureauForm
                creditBureau={creditBureau || risk.creditBureau}
                creditTermValue={creditTermValue}
                onChange={changeCreditBureau}
                annualRevenueValue={annualRevenue?.value}
                noiRateValue={noiRate?.value}
                creditBureauAnualRevenue={creditBureauAnualRevenue}
              />
            </InitialCard>
          </Col>
        </Row>

        <ScoreFromAdminRisk
          data={replyScore}
          onChange={changeReplyScore}
          isFixedReplies={isFixed}
        />

        <Row className={clsx(cls.row, cls.marginTop)}>
          <Col xs={24} className={cls.row}>
            <InitialCard left="Financial Ratios" riskPadding>
              {risk.showAddemVariables && (
                <AddemVariablesTable
                  data={addemVariables}
                  onChange={changeAddemVariablesTable}
                  readonly={readonly}
                />
              )}
              <FinancialRatios readonly={readonly} />
            </InitialCard>
          </Col>
        </Row>

        <Row className={clsx(cls.row, cls.marginTop)}>
          <Col xs={24} className={cls.row}>
            <InitialCard left="Other variables" riskPadding
              lockText={risk.isFixed ? fixedRiskText : null}>
              <Row className={cls.row} gutter={{ lg: 24 }}>
                <Col xs={24} xl={12} className={cls.row}>
                  <DropdownTable
                    data={replyOther}
                    onChange={changeReplyOther}
                    isFixedReplies={isFixed}
                  />
                </Col>
                <Col xs={24} xl={12} className={cls.row}>
                  <div className={clsx(
                    cls.colored, cls.bordered, cls.padding, cls.marginTop, cls.borderedIn,
                  )}>
                    <ScoreWidget
                      value={score}
                      percents={score > risk.successPercent ? 90 : 10}
                      className={cls.scoreBottomWidget}
                      onClick={openReport}
                    />
                  </div>
                  <div className={cls.progressWrap}>
                    <Progress
                      percent={risk.riskProgress?.percent}
                      options={risk.riskProgress?.options} />
                  </div>
                </Col>
              </Row>
            </InitialCard>
          </Col>
        </Row>
      </Container>

      <ExportRisk
        creditId={risk.creditId}
        isOpened={isReportOpened}
        setOpened={setReportOpened}
      />
    </>
  );
};
